import React, { useState } from 'react';
import { NavLink as RouterNavLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../components/common/ThemeContext';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../ui/sheet';
import { Separator } from '../ui/separator';
import { Switch } from '../ui/switch';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { Menu, LogOut } from 'lucide-react';
import { coreNavItems, toolsNavItems, userNavItems } from './navItems';
import { getInitials } from './utils';

interface MobileNavProps {
  isNativeTab: boolean;
}

const MobileNav: React.FC<MobileNavProps> = ({ isNativeTab }) => {
  const [isOpen, setIsOpen] = useState(false);
  const auth = useAuth();
  const { isDark, setTheme } = useTheme();

  return (
    <div className="lg:hidden flex items-center gap-2">
      {!isNativeTab && auth.isAuthenticated && (
        <Avatar className="h-8 w-8 ring-1 ring-border/50">
          <AvatarFallback className="bg-primary/10 text-primary text-xs font-medium">
            {getInitials(auth.user?.name, auth.user?.email)}
          </AvatarFallback>
        </Avatar>
      )}
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger asChild>
          <Button variant="ghost" size="icon" aria-label="Menu">
            <Menu size={20} />
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="w-[280px] sm:w-[340px] flex flex-col p-5">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2.5">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary text-primary-foreground font-bold text-xs shadow-sm">
                N
              </div>
              Narcis EvoApp
            </SheetTitle>
          </SheetHeader>

          {/* Navigation section */}
          <div className="mt-6">
            <span className="px-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
              Navigation
            </span>
            <nav className="flex flex-col gap-1 mt-2">
              {coreNavItems.map((item) => (
                <RouterNavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setIsOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm',
                      isActive
                        ? 'bg-primary/10 text-primary font-medium border-l-2 border-primary'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    )
                  }
                >
                  <item.icon className="h-4 w-4" />
                  {item.label}
                </RouterNavLink>
              ))}
            </nav>
          </div>

          {/* Tools section */}
          <div className="mt-4">
            <span className="px-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
              Tools
            </span>
            <nav className="flex flex-col gap-1 mt-2">
              {toolsNavItems.map((item) => (
                <RouterNavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setIsOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm',
                      isActive
                        ? 'bg-primary/10 text-primary font-medium border-l-2 border-primary'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    )
                  }
                >
                  <item.icon className="h-4 w-4" />
                  {item.label}
                </RouterNavLink>
              ))}
            </nav>
          </div>

          {!isNativeTab && (
            <>
              <Separator className="my-3 opacity-50" />
              <nav className="flex flex-col gap-1">
                {userNavItems.map((item) => (
                  <RouterNavLink
                    key={item.to}
                    to={item.to}
                    onClick={() => setIsOpen(false)}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm',
                        isActive
                          ? 'bg-primary/10 text-primary font-medium border-l-2 border-primary'
                          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                      )
                    }
                  >
                    <item.icon className="h-4 w-4" />
                    {item.label}
                  </RouterNavLink>
                ))}
              </nav>
            </>
          )}

          {/* Spacer */}
          <div className="mt-auto" />

          {/* Theme toggle */}
          <div className="flex items-center justify-between px-3 py-2.5">
            <span className="text-sm text-muted-foreground">Dark mode</span>
            <Switch
              checked={isDark}
              onCheckedChange={(checked) => setTheme(checked ? 'dark' : 'light')}
            />
          </div>

          <Separator className="my-2 opacity-50" />

          {/* User info + sign out */}
          {!isNativeTab && auth.isAuthenticated && (
            <div className="space-y-3 pb-2">
              <div className="flex items-center gap-3 px-3">
                <Avatar className="h-9 w-9">
                  <AvatarFallback className="bg-primary/10 text-primary text-sm font-medium">
                    {getInitials(auth.user?.name, auth.user?.email)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex flex-col min-w-0">
                  {auth.user?.name && (
                    <span className="text-sm font-medium truncate">
                      {auth.user.name}
                    </span>
                  )}
                  <span className="text-xs text-muted-foreground truncate">
                    {auth.user?.email}
                  </span>
                </div>
              </div>
              <Button
                variant="ghost"
                className="w-full justify-start gap-2 text-destructive/80 hover:text-destructive hover:bg-destructive/10"
                onClick={() => {
                  setIsOpen(false);
                  auth.signOut();
                }}
              >
                <LogOut className="h-4 w-4" />
                Sign out
              </Button>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default MobileNav;
